import React, {
  useState,
  useEffect,
  createContext,
  useMemo,
  useCallback,
} from 'react';
import { Consumable } from '@/types/database/communityDevice';
import { MenuCoVendor } from '@/types/database/menuCoVendor';
import { Table, Button, Message } from '@alifd/next';
import Box from '@mui/material/Box';
import client from '@/lib/feathers';
import DownloadExcel from './components/DownloadExcel';
import EditDialog from './components/EditDialog';
import moment from 'moment';
import PageHeader from '@/components/PageHeader';
import UploadExcel from './components/UploadExcel';
import RecordDetail from './components/RecordDetail';
import { handleDeleteDevice } from './utils';

export const VendorContext = createContext<MenuCoVendor[]>([]);

const Consumables: React.FC<{ showHeader: boolean }> = ({
  showHeader = false,
}) => {
  const [records, setRecords] = useState<Consumable[]>([]);
  const [currentRecord, setCurrentRecord] = useState<Consumable>();
  const [editVisible, setEditVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [vendors, setVendors] = useState<MenuCoVendor[]>([]);
  const [trigger, setTrigger] = useState<Record<string, never>>({});

  const columns = useMemo(
    () => [
      {
        dataIndex: 'createdTime',
        title: '建立時間',
        cell: (val: number) => moment(val).format('YYYY-MM-DD'),
      },
      {
        dataIndex: 'deviceName',
        title: '耗材名稱',
      },
      {
        dataIndex: 'deviceKeeper',
        title: '保管人',
      },
      {
        dataIndex: 'deviceDate',
        title: '購入日期',
        cell: (val: number) => moment(val).format('YYYY-MM-DD'),
      },
      {
        title: '操作',
        cell: (_: never, _2: never, record: Consumable) => {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
              }}
            >
              <Button
                onClick={() => {
                  setCurrentRecord(record);
                  setEditVisible(true);
                }}
              >
                編輯
              </Button>
              <Button
                onClick={() => {
                  setCurrentRecord(record);
                  setDetailVisible(true);
                }}
              >
                查看更多
              </Button>
              <Button
                warning
                onClick={async () => {
                  try {
                    await handleDeleteDevice(record);
                    Message.success('刪除成功');
                    setTrigger({});
                  } catch (err) {
                    console.error(err);
                    Message.error(err.message);
                  }
                }}
              >
                刪除
              </Button>
            </Box>
          );
        },
      },
    ],
    []
  );

  const getConsumables = useCallback(async () => {
    try {
      const res = await client.service('communityDevice').find({
        query: {
          deviceType: '消耗用品',
        },
      });
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getVendors = useCallback(async () => {
    try {
      const res = await client
        .service('menuCoVendor')
        .find({ query: { activated: true } });
      setVendors(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    getConsumables();
    getVendors();
  }, [getConsumables, getVendors, trigger]);

  return (
    <VendorContext.Provider value={vendors}>
      <Box>
        {showHeader && (
          <PageHeader
            title="資耗材設備管理(庫存)"
            breadcrumbs={[
              { name: '資產管理' },
              { name: '資耗材設備管理(庫存)' },
            ]}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box
            id="consumables-func"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
            }}
          >
            <Button
              onClick={() => {
                setCurrentRecord(undefined);
                setEditVisible(true);
              }}
            >
              新增耗材
            </Button>
            <DownloadExcel example>下載範例表單</DownloadExcel>
            <DownloadExcel>匯出Excel</DownloadExcel>
            <UploadExcel setTrigger={setTrigger} />
          </Box>
          <Box
            id="consumables-table"
            sx={{
              background: '#fff',
              borderRadius: '5px',
              padding: '10px',
            }}
          >
            <Table dataSource={records} columns={columns} />
          </Box>
        </Box>
        <EditDialog
          visible={editVisible}
          setVisible={setEditVisible}
          record={currentRecord}
          setTrigger={setTrigger}
        />
        <RecordDetail
          visible={detailVisible}
          setVisible={setDetailVisible}
          record={currentRecord}
        />
      </Box>
    </VendorContext.Provider>
  );
};

export default Consumables;
